import { useParams, Link, Navigate } from "react-router-dom";
import { Printer, Download, ArrowLeft, ArrowRight, AlertTriangle, CheckCircle2, ShieldCheck, Droplets, Zap, Flame } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const B = ({ t, cls = "text-ink font-semibold" }) => <>{t.split(/(\*\*[^*]+\*\*)/g).map((s, i) => s.startsWith("**") ? <strong key={i} className={cls}>{s.slice(2, -2)}</strong> : s)}</>;

export const VERTICALS = {
  "oil-gas": {
    icon: Zap, sector: "Oil & Gas", image: IMAGES.pumpjack,
    headline: "Keep Rigs Moving on Any Ground.",
    sub: "Upstream and field operations demand reliable access over soft, remote terrain — and zero contamination near sensitive sites.",
    chips: [["600 psi", "bearing capacity"], ["0%", "fluid absorption"]],
    pains: ["**Soft, remote pads** bog down heavy equipment.", "**Spill exposure** near sensitive ground.", "**Slow mobilization** to dispersed sites."],
    solutions: [
      { title: "Access", rows: ["**High-psi** support on low-CBR soil.", "Machine-set **fast-connect** decks."] },
      { title: "Containment", rows: ["**Non-porous** — no fluid leaching.", "**Washdown-ready** decontamination."] },
      { title: "Utilization", rows: ["**Relocate** rig-to-rig instantly.", "**Years** of reuse, no re-buys."] },
    ],
    outcomes: [["Uptime", "equipment on stable ground"], ["Compliance", "protected sensitive sites"], ["Lower TCO", "fewer assets, zero re-buys"]],
  },
  "pipeline-row": {
    icon: ShieldCheck, sector: "Pipeline ROW", image: IMAGES.pipeline,
    headline: "Protect the Right-of-Way. Hand It Back Clean.",
    sub: "Right-of-way and integrity work over saturated corridors — where rutting, runoff and restoration costs stack up fast.",
    chips: [["0", "replacements / project"], ["100%", "recyclable"]],
    pains: ["**Rutting & runoff** on saturated ROW.", "**Composite breakage** mid-spread.", "**Restoration fines** at handback."],
    solutions: [
      { title: "Structure", rows: ["Distributes load on **soft subgrade.**", "**No rutting** under tracked loads."] },
      { title: "Environment", rows: ["**Non-porous**, zero-leach surface.", "**Clean handback**, minimal restoration."] },
      { title: "Logistics", rows: ["**Move across spreads** with the crew.", "**Reused** on the next project."] },
    ],
    outcomes: [["Protected subgrade", "structural predictability"], ["Clean ROW", "reduced restoration"], ["Zero re-buys", "lower lifecycle spend"]],
  },
  "utility-td": {
    icon: Zap, sector: "Utility / T&D", image: IMAGES.fiber,
    headline: "Mobile Crews. Sensitive Terrain. Rapid Access.",
    sub: "Underground and overhead utility work across dispersed, public and sensitive terrain — with tight windows and live hazards.",
    chips: [["1-Shift", "lay & pull"], ["0", "trip hazards"]],
    pains: ["**Sensitive & public terrain** access.", "**Live-hazard zones** near energized lines.", "**Slow, multi-piece** matting."],
    solutions: [
      { title: "Rapid Access", rows: ["**Fast-connect**, few-piece decks.", "Machine-set — **crews clear.**"] },
      { title: "Safety", rows: ["**Flush, trip-free** surface.", "**Fewer minutes** in the hazard zone."] },
      { title: "Versatility", rows: ["**Walkway, pad, or deck.**", "Reconfigure **on the fly.**"] },
    ],
    outcomes: [["Fewer exposure hours", "crew safety"], ["Fast mobilization", "hit tight windows"], ["Any terrain", "always deployed"]],
  },
  "fire-hazmat": {
    icon: Flame, sector: "Fire / Hazmat", image: IMAGES.fire,
    headline: "Rapid, Non-Contaminating Ground Control.",
    sub: "Response and staging where speed, decontamination and zero secondary contamination are non-negotiable.",
    chips: [["0%", "absorption"], ["100%", "washdown"]],
    pains: ["**Contaminated staging** ground.", "**Porous mats** trap hydrocarbons.", "**Slow set-up** under pressure."],
    solutions: [
      { title: "Decon", rows: ["**Non-porous** — nothing soaks in.", "**100% washdown** decontamination."] },
      { title: "Speed", rows: ["**Rapid** lay-down & pull.", "Machine-set with standard gear."] },
      { title: "Control", rows: ["Stable **staging & access** deck.", "**Reusable** after decon."] },
    ],
    outcomes: [["Zero secondary contamination", "clean sites"], ["Rapid staging", "response speed"], ["Reusable", "lower cost"]],
  },
};

export default function VerticalBrief() {
  const { slug } = useParams();
  const d = VERTICALS[slug];
  if (!d) return <Navigate to="/products/big-bad-mats" replace />;
  const Icon = d.icon;

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">{d.sector} Brief · One-Page</span>
            <a href={`/big-bad-mats-industry-${slug}.pdf`} download data-testid="download-vertical-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors"><Download className="h-4 w-4" /> Download PDF</a>
            <button onClick={() => window.print()} data-testid="print-vertical-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="vertical-brief-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[64%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Big Bad Mats · {d.sector}</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[32px]">{d.headline}</h1>
              <p className="mt-2 text-charcoal text-[11px] leading-snug">{d.sub}</p>
            </div>
            <div className="w-[34%] flex items-stretch gap-2">
              <img src={d.image} alt={d.sector} className="w-1/2 h-24 object-cover border border-border" />
              <div className="w-1/2 bg-ink text-white p-2 grid grid-rows-2 gap-1">
                {d.chips.map(([v, l]) => (
                  <div key={l} className="text-center flex flex-col justify-center">
                    <div className="font-display font-extrabold text-flame text-[18px] leading-none">{v}</div>
                    <div className="text-[7px] uppercase tracking-wider text-white/50 mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </header>

          <div className="flex-1 grid grid-cols-5 gap-4 pt-4 min-h-0">
            <div className="col-span-2 border border-border flex flex-col">
              <div className="bg-charcoal text-white px-4 py-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[12px] uppercase tracking-wider">On This Terrain</span></div>
              <ul className="p-4 space-y-3 flex-1 flex flex-col justify-center">
                {d.pains.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-charcoal text-[11px] leading-snug"><span className="mt-1.5 h-1.5 w-1.5 bg-charcoal/40 flex-shrink-0" /><span><B t={p} /></span></li>
                ))}
              </ul>
            </div>
            <div className="col-span-3 grid grid-rows-3 gap-3">
              {d.solutions.map((s) => (
                <div key={s.title} className="border-2 border-flame flex flex-col">
                  <div className="bg-flame text-white px-4 py-1.5 flex items-center gap-2"><Icon className="h-4 w-4" /><span className="font-display font-bold text-[11.5px] uppercase tracking-wider">{s.title}</span></div>
                  <ul className="px-4 py-2 grid grid-cols-2 gap-x-4 gap-y-1.5 flex-1 content-center">
                    {s.rows.map((x) => (
                      <li key={x} className="flex items-start gap-2 text-charcoal text-[10.5px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" /><span><B t={x} /></span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[13px] uppercase tracking-wider">The Outcome for {d.sector}</span></div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {d.outcomes.map(([v, l]) => (
                <div key={l} className="bg-white/5 border border-white/10 px-4 py-2.5">
                  <div className="font-display font-extrabold text-flame text-[16px] leading-tight">{v}</div>
                  <div className="text-white/60 text-[9.5px] mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div>
              <div className="font-display font-extrabold text-ink text-[15px]">Spec Big Bad Mats for your next {d.sector} project.</div>
              <div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email} · {COMPANY.serviceArea}</div>
            </div>
            <Link to="/request-quote" className="inline-flex items-center gap-1.5 bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Request a Quote <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Performance figures are representative — validated per project. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
